import React from 'react';
import * as yup from 'yup';
import {
    ListTransactionsBankTransactionTypeEnum,
    ListTransactionsCardTransactionTypeEnum,
    ListTransactionsPaymentTypeEnum,
    ListTransactionsStatusEnum,
    ListTransactionsTypeEnum,
    TransactionsApiListTransactionsRequest,
} from '@/api/general';
import {
    AdvanceFilterOptionNew,
    FillerFieldType,
    hasFilterValue,
} from '@/components/FiltersAdvanced/helper';
import { convertDayJsUnixDataToOffsetDateTimeFormat } from '@/helpers/filters';
import { camelCaseToCapitalizedString } from '@/helpers/table';
import { PREFUND_PARTY, SETTLEMENT_PARTY } from '@/helpers/transactionFilters';

export const TRANSACTION_ID_MAX_LENGTH = 36;
// The endpoint parses transactionId and paymentId as UUIDs and rejects anything else with a
// 400, so the format is checked here — otherwise a typo comes back as a failed request with
// an empty table and nothing to explain it.
const UUID_PATTERN =
    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

// The `type` switcher's neutral option. Empty rather than 'all' so that it counts as "no
// filter applied" for the Filter button's badge and the chips row.
export const ALL_TYPES = '';

// The toggle filters store a string, so a checked toggle is this value.
export const TOGGLE_ON = 'Yes';

const toSelectOptions = (values: readonly string[], toLabel: (value: string) => string) =>
    values.map((value) => ({ value, label: toLabel(value) }));

// The chips row prints the raw filter value, which for these is the API's own spelling
// ("authorization", "lockFunds"), so each chip is rendered from the option list instead.
const renderOptionLabel = (options: { value: string; label: string }[]) => (value: string) => (
    <div style={{ textTransform: 'none' }}>
        {options.find((option) => option.value === value)?.label ?? value}
    </div>
);

// The backend spells the card authorisation type the American way; everything the user sees
// spells it the British way, exactly as the transaction row does.
const cardTransactionTypeTitle = (value: string) =>
    camelCaseToCapitalizedString(value === 'authorization' ? 'authorisation' : value);

export const cardTransactionTypeOptions = toSelectOptions(
    Object.values(ListTransactionsCardTransactionTypeEnum),
    cardTransactionTypeTitle,
);

export const bankTransactionTypeOptions = toSelectOptions(
    Object.values(ListTransactionsBankTransactionTypeEnum),
    camelCaseToCapitalizedString,
);

// Payment types are acronyms (SEPA, BACS, TARGET2…), so they keep the casing the API uses.
export const paymentTypeOptions = toSelectOptions(
    Object.values(ListTransactionsPaymentTypeEnum),
    (value) => value,
);

export interface TransactionsFilters {
    type?: string;
    cardTransactionType?: string;
    incremental?: string;
    cardId?: string;
    bankTransactionType?: string;
    paymentType?: string;
    senderRecipient?: string;
    iban?: string;
    transactionId?: string;
    status?: string[];
    paymentId?: string;
    orderReference?: string;
    currency?: string[];
    amountRange?: (string | number)[];
    dateRange?: (string | number)[];
}

/**
 * The three type filters take one value each. The story asks for multi-picklists, but the
 * endpoint delivered by MP-15905 declares them as single enums and answers a repeated query
 * parameter with a 400 (verified against api.scp.int on 21 Sep 2026), so offering more than
 * one value would only produce a failed request and an empty table.
 */
export type TransactionsApiFilters = Omit<
    TransactionsApiListTransactionsRequest,
    'pageNumber' | 'pageSize'
>;

const emptyToUndefined = (value: string | undefined) => value || undefined;
const emptyListToUndefined = <T,>(values: T[] | undefined) =>
    values && values.length > 0 ? values : undefined;

export const buildTransactionsApiFilters = (
    filters: TransactionsFilters = {},
): TransactionsApiFilters => ({
    type: emptyToUndefined(filters.type) as ListTransactionsTypeEnum | undefined,
    transactionId: emptyToUndefined(filters.transactionId),
    paymentId: emptyToUndefined(filters.paymentId),
    orderReference: emptyToUndefined(filters.orderReference),
    cardId: emptyToUndefined(filters.cardId),
    cardTransactionType: emptyToUndefined(filters.cardTransactionType) as
        | ListTransactionsCardTransactionTypeEnum
        | undefined,
    // Purchases and refunds cannot be incremental and the API matches them on
    // `incremental=false`, so an unchecked toggle has to drop the parameter rather than send
    // false — otherwise switching the toggle off would filter the list instead of clearing it.
    incremental: filters.incremental === TOGGLE_ON ? true : undefined,
    bankTransactionType: emptyToUndefined(filters.bankTransactionType) as
        | ListTransactionsBankTransactionTypeEnum
        | undefined,
    paymentType: emptyToUndefined(filters.paymentType) as
        | ListTransactionsPaymentTypeEnum
        | undefined,
    status: emptyListToUndefined(filters.status as ListTransactionsStatusEnum[] | undefined),
    currency: emptyListToUndefined(filters.currency),
    amount:
        filters.amountRange && filters.amountRange.length > 0
            ? `${filters.amountRange[0]}#${filters.amountRange[1]}`
            : undefined,
    createdAtFrom: filters.dateRange?.[0]
        ? convertDayJsUnixDataToOffsetDateTimeFormat(filters.dateRange[0])
        : undefined,
    createdAtTo: filters.dateRange?.[1]
        ? convertDayJsUnixDataToOffsetDateTimeFormat(filters.dateRange[1])
        : undefined,
});

export const transactionsFilterValidationScheme = yup.object().shape({
    type: yup.string().notRequired(),
    cardTransactionType: yup.string().notRequired(),
    incremental: yup.string().notRequired(),
    cardId: yup.string().notRequired(),
    bankTransactionType: yup.string().notRequired(),
    paymentType: yup.string().notRequired(),
    senderRecipient: yup.string().notRequired(),
    iban: yup.string().notRequired(),
    transactionId: yup
        .string()
        // An emptied field means "not filtering by this", so the rules below must not fire on
        // it — otherwise clearing the input leaves an error the user cannot get rid of.
        .transform((value) => (value === '' ? undefined : value))
        .max(
            TRANSACTION_ID_MAX_LENGTH,
            `Transaction ID must be ${TRANSACTION_ID_MAX_LENGTH} characters or less`,
        )
        .test(
            'is-uuid',
            'Enter a valid Transaction ID',
            (value) => !value || UUID_PATTERN.test(value),
        ),
    paymentId: yup
        .string()
        .transform((value) => (value === '' ? undefined : value))
        .test(
            'is-uuid',
            'Enter a valid Payment instruction ID',
            (value) => !value || UUID_PATTERN.test(value),
        ),
    orderReference: yup.string().notRequired(),
    status: yup.array().notRequired(),
    dateRange: yup.array().notRequired(),
    amountRange: yup.array().notRequired(),
    currency: yup.array().when('amountRange', {
        is: (amountRange: any[]) => Array.isArray(amountRange) && amountRange.length > 0,
        then: (schema) => schema.min(1, 'Currency is required for Amount'),
        otherwise: (schema) => schema.notRequired(),
    }),
});

const CARD_GROUP = ['cardTransactionType', 'incremental', 'cardId'];
const BANK_GROUP = ['bankTransactionType', 'paymentType', 'iban', 'senderRecipient'];

const hasAnyValue = (values: Record<string, any>, names: string[]) =>
    names.some((name) => hasFilterValue(values[name]));

// Under Type = All both groups are on screen, and a transaction is either a card or a bank
// one, so filling one group disables the other. A group that already holds values stays
// editable — a link saved before this rule can carry both, and it must be possible to clear.
const disabledByGroup =
    (otherGroup: string[], ownGroup: string[]) => (values: Record<string, any>) =>
        hasAnyValue(values, otherGroup) && !hasAnyValue(values, ownGroup);

const cardGroupDisabled = disabledByGroup(BANK_GROUP, CARD_GROUP);
const bankGroupDisabled = disabledByGroup(CARD_GROUP, BANK_GROUP);

/**
 * Laid out as the design has it: the card-only group, then the bank-only group, then the
 * filters that apply to both. `type` gates the two groups — picking Card hides the bank
 * filters and vice versa, and the blade clears whatever it hides so a hidden value can never
 * reach the request.
 */
export const transactionsFilterOptions = (
    currenciesList: { value: string; label: string }[],
    cardList: any[],
    orderList: any[],
    renderCardLabel: (value: string) => React.ReactNode,
): AdvanceFilterOptionNew[] => [
    {
        fieldType: FillerFieldType.tagSelect,
        name: 'type',
        label: '',
        defaultValue: ALL_TYPES,
        options: [
            { value: ALL_TYPES, label: 'All' },
            { value: ListTransactionsTypeEnum.Card, label: 'Card' },
            { value: ListTransactionsTypeEnum.Bank, label: 'Bank' },
        ],
    },
    {
        fieldType: FillerFieldType.select,
        name: 'cardTransactionType',
        label: 'Card transaction type',
        defaultValue: '',
        options: cardTransactionTypeOptions,
        renderValueFunction: renderOptionLabel(cardTransactionTypeOptions),
        isHideCondition: {
            watchedField: 'type',
            value: [ALL_TYPES, ListTransactionsTypeEnum.Card],
        },
        isDisabled: cardGroupDisabled,
    },
    {
        fieldType: FillerFieldType.toggle,
        name: 'incremental',
        label: 'Incremental',
        defaultValue: '',
        isHideCondition: {
            watchedField: 'type',
            value: [ALL_TYPES, ListTransactionsTypeEnum.Card],
        },
        isDisabled: cardGroupDisabled,
    },
    {
        fieldType: FillerFieldType.cardSelector,
        name: 'cardId',
        label: 'Card',
        defaultValue: '',
        options: cardList,
        renderValueFunction: renderCardLabel,
        isHideCondition: {
            watchedField: 'type',
            value: [ALL_TYPES, ListTransactionsTypeEnum.Card],
        },
        isDisabled: cardGroupDisabled,
    },
    {
        fieldType: FillerFieldType.select,
        name: 'bankTransactionType',
        label: 'Bank transaction type',
        defaultValue: '',
        options: bankTransactionTypeOptions,
        renderValueFunction: renderOptionLabel(bankTransactionTypeOptions),
        isHideCondition: {
            watchedField: 'type',
            value: [ALL_TYPES, ListTransactionsTypeEnum.Bank],
        },
        isDisabled: bankGroupDisabled,
    },
    {
        fieldType: FillerFieldType.select,
        name: 'paymentType',
        label: 'Bank transaction payment type',
        defaultValue: '',
        options: paymentTypeOptions,
        renderValueFunction: renderOptionLabel(paymentTypeOptions),
        isHideCondition: {
            watchedField: 'type',
            value: [ALL_TYPES, ListTransactionsTypeEnum.Bank],
        },
        isDisabled: bankGroupDisabled,
    },
    {
        fieldType: FillerFieldType.input,
        name: 'iban',
        label: 'IBAN / Account number',
        defaultValue: '',
        renderValueFunction: (value: string) => (
            <div style={{ textTransform: 'none' }}>{value}</div>
        ),
        isHideCondition: {
            watchedField: 'type',
            value: [ALL_TYPES, ListTransactionsTypeEnum.Bank],
        },
        isDisabled: bankGroupDisabled,
    },
    {
        fieldType: FillerFieldType.select,
        name: 'senderRecipient',
        label: 'Sender / Recipient',
        defaultValue: '',
        options: [
            { value: SETTLEMENT_PARTY, label: SETTLEMENT_PARTY },
            { value: PREFUND_PARTY, label: PREFUND_PARTY },
        ],
        isHideCondition: {
            watchedField: 'type',
            value: [ALL_TYPES, ListTransactionsTypeEnum.Bank],
        },
        isDisabled: bankGroupDisabled,
    },
    {
        fieldType: FillerFieldType.input,
        name: 'transactionId',
        label: 'Transaction ID',
        defaultValue: '',
        renderValueFunction: (value: string) => (
            <div style={{ textTransform: 'none' }}>{value}</div>
        ),
    },
    {
        fieldType: FillerFieldType.multiSelect,
        name: 'status',
        label: 'Status',
        defaultValue: [],
        options: Object.values(ListTransactionsStatusEnum).map((value) => ({
            value,
            label: camelCaseToCapitalizedString(value),
        })),
    },
    {
        fieldType: FillerFieldType.input,
        name: 'paymentId',
        label: 'Payment instruction',
        defaultValue: '',
        renderValueFunction: (value: string) => (
            <div style={{ textTransform: 'none' }}>{value}</div>
        ),
    },
    {
        fieldType: FillerFieldType.orderSelector,
        name: 'orderReference',
        label: 'Order',
        defaultValue: '',
        options: orderList,
    },
    {
        fieldType: FillerFieldType.tagMultiSelect,
        name: 'currency',
        label: 'Currency',
        defaultValue: [],
        options: currenciesList,
    },
    {
        fieldType: FillerFieldType.amountRange,
        name: 'amountRange',
        label: 'Amount range',
        defaultValue: [],
        fieldToTriggerAfterChange: 'currency',
    },
    {
        fieldType: FillerFieldType.dateRangePicker,
        name: 'dateRange',
        label: 'Date range',
        defaultValue: [],
    },
];
